import { Theme } from "ipctg-inflow-types/InFlowThemes"
import {
  PropsWithChildren,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"

import { log } from "../logging"
import { DocumentThemeMode, ScannedArea } from "../types/local"

import { ThemeContext } from "./useTheme"

export function ThemeProvider(props: PropsWithChildren) {
  const [scannedArea, setScannedArea] = useState<ScannedArea>()
  const [documentThemeMode, setDocumentThemeMode] =
    useState<DocumentThemeMode>("root")

  useLayoutEffect(() => {
    const styleID = "white-label-styles"
    let styleElement = document.getElementById(styleID) as HTMLStyleElement
    if (!styleElement) {
      styleElement = document.createElement("style")
      styleElement.id = styleID
      document.head.appendChild(styleElement)
    }

    styleElement.textContent = generateWhiteLabelStyles(scannedArea)

    function generateWhiteLabelStyles(scannedArea?: ScannedArea) {
      if (!scannedArea) return ""
      return generateThemeStyles(scannedArea.theme.light)

      function generateThemeStyles(theme: Theme) {
        const variables = Object.entries(theme.hues).flatMap(
          ([hue, swatches]) =>
            Object.entries(swatches).map(
              ([swatch, color]) => `--${hue}-${swatch}: ${color};`
            )
        )
        return `html[data-use-root-theme="false"], html *[data-use-root-theme="false"] {
          ${variables.join("\n      ")}
        }\n`
      }
    }
  }, [scannedArea])

  useLayoutEffect(() => {
    document.documentElement.setAttribute(
      "data-use-root-theme",
      documentThemeMode === "root" ? "true" : "false"
    )
    return () => {
      document.documentElement.removeAttribute("data-use-root-theme")
    }
  }, [documentThemeMode])

  const handleDocumentThemeModeChange = useCallback(
    (mode: DocumentThemeMode) => {
      log({
        level: "info",
        scope: "info",
        flow: "misc",
        message: `Document theme mode changed to: ${mode}`,
      })
      setDocumentThemeMode(mode)
    },
    []
  )

  /*
    The scanned area's theme is split into its top level slices so consumers
    can reach for what they need directly, rather than walking the whole
    SambaTheme. `theme` is the applied theme, which is always the light one.
  */
  const value = useMemo(
    () => ({
      qrLookupID: scannedArea?.qrLookupID,
      theme: scannedArea?.theme.light,
      locationInformation: scannedArea?.theme.locationInformation,
      welcome: scannedArea?.theme.welcome,
      drawer: scannedArea?.theme.drawer,
      metadata: scannedArea?.theme.metadata,
      setScannedArea,
      documentThemeMode,
      setDocumentThemeMode: handleDocumentThemeModeChange,
    }),
    [scannedArea, documentThemeMode, handleDocumentThemeModeChange]
  )

  return (
    <ThemeContext.Provider value={value}>
      {props.children}
    </ThemeContext.Provider>
  )
}
